import React, { useState } from 'react';
import { Modal, Spin } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import type { MediaItem } from '../types/media';

interface MediaPreviewModalProps {
  visible: boolean;
  onClose: () => void;
  media: MediaItem | null;
  videoUrl?: string; // 추가/편집 화면에서 임시 URL 전달용
}

const MediaPreviewModal: React.FC<MediaPreviewModalProps> = ({
  visible,
  onClose,
  media,
  videoUrl,
}) => {
  const [videoLoading, setVideoLoading] = useState(true);
  const [videoError, setVideoError] = useState(false);

  // 비디오 URL 결정
  const getVideoUrl = (): string | null => {
    // 임시 URL이 제공된 경우 (추가/편집 화면)
    if (videoUrl) {
      return videoUrl;
    }

    // MediaItem에서 URL 가져오기
    if (media?.videoUrl) {
      return media.videoUrl;
    }

    // 파일명으로 URL 생성
    if (media?.videoFile) {
      return `/uploads/videos/${media.videoFile}`;
    }

    return null;
  };

  const currentVideoUrl = getVideoUrl();

  if (!media && !videoUrl) return null;

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1024}
      className="preview-modal"
      closeIcon={<CloseOutlined style={{ color: '#ffffff', fontSize: '20px' }} />}
      styles={{
        body: {
          padding: 0,
          background: '#000000',
          margin: 0,
          minHeight: '288px',
          maxHeight: '288px'
        },
        content: {
          padding: 0
        },
        mask: {
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(10px)'
        }
      }}
      centered
      destroyOnHidden={true}
      maskClosable={true}
    >
      <div
        style={{
          display: 'flex',
          width: '1024px',
          height: '288px',
          background: '#0E0E0E',
          position: 'relative',
          boxSizing: 'border-box',
          boxShadow: '0 0 0 0.2px #fff'
        }}
      >
        {!currentVideoUrl ? (
          <div style={{
            color: '#a6a6a6',
            fontSize: '16px',
            textAlign: 'center'
          }}>
            비디오 파일을 업로드해주세요.
          </div>
        ) : (
          <>
            {videoLoading && !videoError && (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 2,
                textAlign: 'center'
              }}>
                <Spin size="large" />
                <div style={{ color: '#a6a6a6', marginTop: '16px', fontSize: '14px' }}>
                  비디오 로딩 중...
                </div>
              </div>
            )}

            {videoError ? (
              <div style={{
                color: '#a6a6a6',
                fontSize: '16px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
                <div>비디오를 불러올 수 없습니다.</div>
              </div>
            ) : (
              <video
                controls
                autoPlay
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: videoLoading ? 'none' : 'block'
                }}
                onLoadStart={() => {
                  setVideoLoading(true);
                  setVideoError(false);
                }}
                onCanPlay={() => setVideoLoading(false)}
                onError={() => {
                  setVideoLoading(false);
                  setVideoError(true);
                }}
              >
                <source src={currentVideoUrl} type="video/mp4" />
                브라우저가 비디오 재생을 지원하지 않습니다.
              </video>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default MediaPreviewModal;
